import {
    FINAL_TRAINING_STATUSES,
    isEvaluationPhaseTrainingStatus,
} from '@/features/trainings/constants';
import {
    distillation_in_progress,
    evaluation_in_progress,
} from '@/shared/constants/taskStatuses';

const hasConfiguredValue = (value) => {
    if (typeof value === 'string') return value.trim().length > 0;
    return value != null && value !== false;
};

export const isFinalTrainingStatus = (status) =>
    FINAL_TRAINING_STATUSES.includes(String(status || '').toLowerCase());

function pickNewestTraining(candidates) {
    if (!candidates?.length) return null;
    return candidates.reduce((latest, current) => {
        const latestId = latest?.distillation_event_id || '';
        const currentId = current?.distillation_event_id || '';
        return currentId > latestId ? current : latest;
    }, candidates[0]);
}

/**
 * Best-effort active training row for a usecase from WebSocket-backed `trainStatuses`.
 * TaskPage's ActionRowList may merge in REST-fetched training; pass that via
 * `computeActionStatusById` options when available.
 */
export function deriveActiveTrainingFromStatuses(usecase, trainStatuses) {
    if (!usecase?._id) return null;
    const candidates = Object.values(trainStatuses || {}).filter((training) => {
        if (!training) return false;
        const usecaseId = training.usecase_id || training.llm_usecase_id;
        if (usecaseId !== usecase._id) return false;
        return !isFinalTrainingStatus(training?.status);
    });
    return pickNewestTraining(candidates);
}

/**
 * Step indicator map keyed like `TASK_ACTIONS[].id`. Values: `completed` | `loading` | `warning` | null.
 * Mirrors `ActionRowList` so tab strip dots stay consistent with the action cards.
 *
 * @param {Object|null|undefined} options.activeTraining - When provided (including `null`), used as-is.
 *   When omitted, derived from `trainStatuses` only.
 */
export function computeActionStatusById(usecase, options = {}) {
    const {
        trainStatuses = {},
        progressStatus = {},
        evaluationResultsReady = {},
        hasEvaluationResults = false,
    } = options;

    const activeTraining =
        'activeTraining' in options
            ? options.activeTraining
            : deriveActiveTrainingFromStatuses(usecase, trainStatuses);

    const hasActiveTraining = Boolean(activeTraining) ||
        usecase?.status === distillation_in_progress ||
        usecase?.status === evaluation_in_progress;

    const hasActiveUpload = Boolean(usecase?._id) &&
        Object.values(progressStatus || {}).some((entry) => {
            if (!entry || entry.isDownload) return false;
            const touches =
                String(entry.usecaseId ?? '') === String(usecase._id ?? '') ||
                (
                    Array.isArray(entry.affectedUsecaseIds) &&
                    entry.affectedUsecaseIds.some((id) => String(id) === String(usecase._id))
                );
            if (!touches) return false;
            return !entry.isCompleted && !entry.isError;
        });

    const comparisonOn = Boolean(usecase?.comparison_mode);
    const hasPrompt = hasConfiguredValue(usecase?.prompt);
    const hasTeacherModel = hasConfiguredValue(usecase?.teacher_model);
    const hasParserFunction = hasConfiguredValue(
        usecase?.parser_function || usecase?.parserFunction
    );
    const hasParserValidationFailure = Boolean(usecase?.parser_function_validation_failed);
    const modelConfigurationCompleted = hasPrompt && (
        !comparisonOn || (hasTeacherModel && hasParserFunction)
    );

    const modelConfigurationWarningParts = [];
    if (comparisonOn) {
        if (!hasPrompt && !hasTeacherModel) {
            modelConfigurationWarningParts.push(
                'Comparison mode is enabled but no prompt or teacher model are selected.'
            );
        }
        else if (!hasPrompt) {
            modelConfigurationWarningParts.push(
                'Comparison mode is enabled but no prompt is selected.'
            );
        }
        else if (!hasTeacherModel) {
            modelConfigurationWarningParts.push(
                'Comparison mode is enabled but no teacher model is selected.'
            );
        }
        else if (!hasParserFunction) {
            modelConfigurationWarningParts.push(
                'Comparison mode is enabled but no response parser function is selected.'
            );
        }
    }
    if (hasParserValidationFailure) {
        modelConfigurationWarningParts.push(
            usecase?.parser_function_validation_message ||
            'Response parser validation failed. Update the prompt or parser function, then validate again.'
        );
    }
    const modelConfigurationWarningMessage =
        modelConfigurationWarningParts.length > 0
            ? modelConfigurationWarningParts.join(' ')
            : null;
    const modelConfigurationWarning = Boolean(modelConfigurationWarningMessage);

    const dataEditorCompleted = Array.isArray(usecase?.datasets) && usecase.datasets.length > 0;
    const trainingAndEvaluationReady = hasEvaluationResults && !hasActiveTraining;

    const isActiveEvaluationPhase = hasActiveTraining &&
        (
            isEvaluationPhaseTrainingStatus(activeTraining?.status) ||
            (!activeTraining?.status && usecase?.status === evaluation_in_progress)
        );
    // Dataset upload/processing is part of the Data Editor flow — do not treat it as training.
    const trainingStatusLoading = hasActiveTraining && !isActiveEvaluationPhase;
    const evaluationStatusLoading = hasActiveTraining && isActiveEvaluationPhase;

    const evaluationSignalExists = Boolean(usecase?._id) &&
        Object.keys(evaluationResultsReady || {}).some((key) =>
            key.startsWith(`${usecase._id}_`)
        );

    const actionStatusById = {
        'model-configuration': modelConfigurationWarning
            ? 'warning'
            : modelConfigurationCompleted
                ? 'completed'
                : null,
        'data-editor': hasActiveUpload
            ? 'loading'
            : dataEditorCompleted
                ? 'completed'
                : null,
        training: trainingStatusLoading
            ? 'loading'
            : isActiveEvaluationPhase
                ? null
                : (trainingAndEvaluationReady || evaluationSignalExists)
                    ? 'completed'
                    : null,
        evaluations: evaluationStatusLoading
            ? 'loading'
            : (trainingAndEvaluationReady || evaluationSignalExists)
                ? 'completed'
                : null,
    };

    return {
        actionStatusById,
        modelConfigurationWarningMessage,
    };
}

export const mergeActiveTrainingFromWsAndFetch = ({
    activeTrainingFromFetch,
    activeTrainingFromWs,
    trainStatuses,
}) => {
    const knownId = activeTrainingFromFetch?.distillation_event_id;
    if (knownId) {
        const latestKnown = trainStatuses?.[knownId];
        if (latestKnown) {
            if (isFinalTrainingStatus(latestKnown.status)) {
                return null;
            }
            return {
                ...activeTrainingFromFetch,
                ...latestKnown,
            };
        }
    }

    if (activeTrainingFromWs) {
        return {
            ...activeTrainingFromFetch,
            ...activeTrainingFromWs,
        };
    }
    return activeTrainingFromFetch;
};
